import {
	ContentLoader,
	FlexAlign,
	FlexGap,
	FlexJustify,
	StackVertical,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import React, { useEffect, useState } from 'react';
import { getShippingOptions } from '@ukpostbox/client-data';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTruckFast } from '@fortawesome/pro-solid-svg-icons/faTruckFast';
import { useSite } from '../../_Global/Providers/SiteInterfaceProvider';
import { ShippingOptionsType } from '../Types/ShippingOptionsTypes';
import { ShippingOptionsResultGroup } from './ShippingOptionsResultGroup';

function ShippingOptions(props: ShippingOptionsType) {
	const { t } = useTranslation();
	const { UpdateSiteContact } = useSite();

	const [shippingOptionsLoading, setShippingOptionsLoading] = useState<boolean>(false);
	const [shippingOptions, setShippingOptions] = useState<any>(null);
	const [groupData, setGroupData] = useState<any>(null);

	const FetchOptions = async () => {
		setShippingOptionsLoading(true);
		await getShippingOptions(props.countryCode, props.width, props.length, props.depth, props.weight)
			.then((r) => {
				setShippingOptions(r);
			})
			.finally(() => setShippingOptionsLoading(false));
	};

	const GroupData = (data: any) => {
		const groups = data.reduce((thisGroup: any, item: any) => {
			const title = item.description.split(' ')[0];

			if (!thisGroup[title]) {
				// eslint-disable-next-line no-param-reassign
				thisGroup[title] = [];
			}

			thisGroup[title].push(item);

			return thisGroup;
		}, {});

		setGroupData(groups);
	};

	const HandleShippingQuoteBuilder = () => {
		UpdateSiteContact(
			true,
			1,
			`Requesting a shipping cost quote for:\n\nDestination Country:  ${props.countryCode} \nWidth:  ${props.width} \nLength:  ${props.length} \nDepth:  ${props.depth} \nWeight:  ${props.weight}`,
		);
	};

	useEffect(() => {
		if (shippingOptions?.data) {
			GroupData(shippingOptions.data);
		}
	}, [shippingOptions]);

	useEffect(() => {
		FetchOptions().then(() => null);
	}, [props.updateValues]);

	return (
		<>
			{(!groupData || shippingOptionsLoading) && <ContentLoader />}

			{groupData && !shippingOptionsLoading && Object.keys(groupData).length === 0 && (
				<StackVertical
					gap={FlexGap.ExtraSmall}
					align={FlexAlign.Center}
					justify={FlexJustify.Center}
					extraClass='no-content-border m-xxl p-xxl text-center'
				>
					<FontAwesomeIcon icon={faTruckFast} size='3x' />
					<h6 className='sub-heading mt-sm mb-xs'>{t('shippingCalculator.noData.title')}</h6>
					<p>{t('shippingCalculator.noData.description')}</p>
				</StackVertical>
			)}

			{groupData && Object.keys(groupData).length > 0 && (
				<>
					{Object.keys(groupData).map((key) => (
						<ShippingOptionsResultGroup
							title={key}
							data={groupData[key]}
							callback={() => HandleShippingQuoteBuilder()}
						/>
					))}

					<ol>
						<li>All shipping rates are shown in Great Britain Pounds (GBP).</li>
						<li>
							These prices include UK VAT @ 20%, but if you’re outside of the UK and operate as an
							Incorporated Business or Registered Charity, you won’t be charged VAT.
						</li>
						<li>Our Shipping Calculator provides an estimate of the shipping total cost.</li>
						<li>
							All shipping cost quoted are DDU (Delivery Duty Unpaid), unless explicitly stated. You may
							be liable for VAT/Duty on delivery of your items/goods in the recipients destination
							country.
						</li>
						<li>
							We will calculate the final shipping total cost when forwarding your items based on the
							actual weight and dimensions of your package.
						</li>
						<li>
							All services may be subject to the following additional charges depending on your item
							specifics and destination, including but not limited to:
							<ol className='roman-numeral-list'>
								<li>Elevated Risk & Restricted Destination Surcharge</li>
								<li>Emergency Situation Surcharge</li>
								<li>Export Validation</li>
								<li>Parcel Handling / Oversized Piece</li>
								<li>Air Carrier Supplement Charges</li>
							</ol>
						</li>
					</ol>
				</>
			)}
		</>
	);
}

export { ShippingOptions };
